import { keepPreviousData, useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { worldCupQueryKeys } from '../constants/worldcupQueryKeys';
import { SQUAD_LEADERBOARD_PAGE_SIZE } from '../helpers/squadLeaderboardPagination.helper';
import {
  GC_TIME_WORLD_CUP_MS,
  STALE_TIME_WORLD_CUP_LEADERBOARD_MS,
} from '../constants/worldcupReactQuery.constants';
import type {
  WorldCupGroupCompanyBranding,
  WorldCupMembershipRole,
} from '../types/worldcup.types';

export interface GroupLeaderboardApiEntry {
  id: string;
  userId: string;
  role?: WorldCupMembershipRole;
  name: string;
  totalPoints: number;
  /** Provisional total (settled + live points) used for live squad re-ranking. */
  provisionalPoints?: number;
  /** Points currently being earned from in-progress matches. */
  livePoints?: number;
  exactHitCount: number;
  correctResultCount: number;
  winnerCorrect: boolean | null;
  topScorerCorrect: boolean | null;
  winnerTeamId?: string | null;
  topScorerPlayerId?: string | null;
  avatarId?: string | null;
  avatar?: string | null;
  isCurrentUser: boolean;
  rank: number;
  livePredictions?: {
    matchId: string;
    competitionMatchId: string | null;
    homeTeam: string;
    awayTeam: string;
    homeCountryCode: string;
    awayCountryCode: string;
    liveMinute: number | null;
    kickoffTime: string;
    displayOrder: number | null;
    stage: string;
    prediction: {
      home: number;
      away: number;
    };
    outcome: 'hit' | 'result' | 'miss';
    pointsEarned: number;
    isDoublePoints: boolean;
  }[];
}

interface GroupLeaderboardApiPayload {
  success: boolean;
  error?: string;
  data: {
    group?: {
      id: string;
      name: string;
      inviteCode?: string | null;
      companyBranding?: WorldCupGroupCompanyBranding | null;
      /** Present when the authenticated user is an ACTIVE member; drives squad rename affordance. */
      viewerRole?: 'ADMIN' | 'MEMBER' | null;
      viewerCanLeaveSquad?: boolean;
      crestColor?: string | null;
      crestEmblem?: string | null;
    };
    entries: GroupLeaderboardApiEntry[];
    currentUser: GroupLeaderboardApiEntry | null;
    currentUserRole?: WorldCupMembershipRole | null;
    stageMvp: unknown;
    pagination: { page: number; limit: number; total: number; totalPages: number };
  };
}

export type GroupLeaderboardPeriod = 'overall' | 'week';

export function worldCupGroupLeaderboardQueryOptions(
  apiClient: ReturnType<typeof useApiClient>,
  competitionId: string,
  groupId: string,
  limit: number = 100,
  period: GroupLeaderboardPeriod = 'overall',
) {
  return {
    queryKey: worldCupQueryKeys.groupLeaderboard(competitionId, groupId, { limit, period }),
    queryFn: async () => {
      const response = await apiClient<GroupLeaderboardApiPayload>(
        `/competitions/${competitionId}/groups/${groupId}/leaderboard?limit=${limit}&period=${period}`,
      );
      const payload = response.data;
      if (!payload.success) {
        throw new Error(payload.error || 'Failed to fetch group leaderboard');
      }
      return payload.data;
    },
    staleTime: STALE_TIME_WORLD_CUP_LEADERBOARD_MS,
    gcTime: GC_TIME_WORLD_CUP_MS,
    refetchOnWindowFocus: false,
    // Keep the previous period's payload on screen while the new one is in
    // flight so the page chrome (hero banner, toggle) doesn't blank out on a
    // Week ↔ Overall switch. The page reads `isPlaceholderData` to know when
    // to swap the section to a skeleton.
    placeholderData: keepPreviousData,
  };
}

export function useWorldCupGroupLeaderboardQuery(
  competitionId: string,
  groupId: string,
  options?: { enabled?: boolean; limit?: number; period?: GroupLeaderboardPeriod },
) {
  const apiClient = useApiClient();
  const limit = options?.limit ?? 100;
  const period = options?.period ?? 'overall';
  const enabled = (options?.enabled ?? true) && groupId.length > 0;

  return useQuery({
    ...worldCupGroupLeaderboardQueryOptions(apiClient, competitionId, groupId, limit, period),
    enabled,
  });
}

/**
 * Query options for the paginated (infinite) squad leaderboard. Each page is one
 * `GET …/leaderboard?page&limit&period` response; `getNextPageParam` advances until
 * the last page (`page >= totalPages`). Shared so callers can also
 * `prefetchInfiniteQuery` the inactive period (Week ↔ Overall warm cache).
 */
export function worldCupGroupLeaderboardInfiniteQueryOptions(
  apiClient: ReturnType<typeof useApiClient>,
  competitionId: string,
  groupId: string,
  limit: number = SQUAD_LEADERBOARD_PAGE_SIZE,
  period: GroupLeaderboardPeriod = 'overall',
) {
  return {
    queryKey: worldCupQueryKeys.groupLeaderboardInfinite(competitionId, groupId, { limit, period }),
    initialPageParam: 1,
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const response = await apiClient<GroupLeaderboardApiPayload>(
        `/competitions/${competitionId}/groups/${groupId}/leaderboard?limit=${limit}&page=${pageParam}&period=${period}`,
      );
      const payload = response.data;
      if (!payload.success) {
        throw new Error(payload.error || 'Failed to fetch group leaderboard');
      }
      return payload.data;
    },
    getNextPageParam: (lastPage: GroupLeaderboardApiPayload['data']) => {
      const { page, totalPages } = lastPage.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    staleTime: STALE_TIME_WORLD_CUP_LEADERBOARD_MS,
    gcTime: GC_TIME_WORLD_CUP_MS,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  };
}

export function useWorldCupGroupLeaderboardInfiniteQuery(
  competitionId: string,
  groupId: string,
  options?: { enabled?: boolean; limit?: number; period?: GroupLeaderboardPeriod },
) {
  const apiClient = useApiClient();
  const limit = options?.limit ?? SQUAD_LEADERBOARD_PAGE_SIZE;
  const period = options?.period ?? 'overall';
  const enabled = (options?.enabled ?? true) && groupId.length > 0;

  return useInfiniteQuery({
    ...worldCupGroupLeaderboardInfiniteQueryOptions(apiClient, competitionId, groupId, limit, period),
    enabled,
  });
}
